import React from 'react';
import * as m from 'motion/react-m';

import { usePrefersReduced } from './useMotionPreference';

/** Seconds between each child's reveal. */
const STAGGER_CHILDREN = 0.08;

export interface StaggerGroupProps {
  children?: React.ReactNode;
  /** Optional className passed through to the motion element. */
  className?: string;
}

/**
 * Variants parent for staggered list/grid reveals. Drives a `hidden`→`visible`
 * orchestration on scroll-into-view; children (e.g. `<Reveal>`-style items
 * using the matching `hidden`/`visible` variant names) inherit the state and
 * animate in sequence via `staggerChildren`.
 *
 * Reduced-motion branch: when `usePrefersReduced()` is true the stagger delay
 * collapses to 0, so all children settle at once with no cascading movement.
 */
export const StaggerGroup: React.FC<StaggerGroupProps> = ({
  children,
  ...rest
}) => {
  const prefersReduced = usePrefersReduced();

  const variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : STAGGER_CHILDREN,
      },
    },
  };

  return (
    <m.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={variants}
      {...rest}
    >
      {children}
    </m.div>
  );
};

export default StaggerGroup;
